/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { DeleteOutlined } from '@ant-design/icons'
import { Input as AntInput, InputRef } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { normalizeOptionLabel } from '@sportninja/common/utils/optionLabel'
import { Option } from './types'
import { MAX_OPTION_LABEL_LENGTH } from './optionListText'

interface OptionRowProps {
  option: Option
  index: number
  /** False for the last remaining option — a dropdown always needs one. */
  canDelete: boolean
  onLabelChange: (id: string, label: string) => void
  onDelete: (id: string) => void
  /** Enter on the LAST row with content appends a new one. */
  onEnter: (id: string) => void
  setInputRef: (id: string) => (ref: InputRef | null) => void
}

/**
 * One editable option row.
 *
 * Extracted from OptionEditor so a 105-option list does not rebuild every row's
 * input on each keystroke. Memoization comes from the React Compiler via the
 * 'use memo' directive, not from a manual React.memo wrapper.
 */
const OptionRow = ({
  option,
  index,
  canDelete,
  onLabelChange,
  onDelete,
  onEnter,
  setInputRef,
}: OptionRowProps) => {
  'use memo'

  // Measured on the canonical label, because that is what is emitted — padding
  // and repeated inner whitespace never reach the varchar(191) column, so
  // neither may turn the row red.
  const isTooLong =
    normalizeOptionLabel(option.label).length > MAX_OPTION_LABEL_LENGTH

  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        gap: 8px;
      `}
    >
      <div
        css={css`
          flex: 1;
        `}
      >
        <AntInput
          ref={setInputRef(option.id)}
          value={option.label}
          onChange={(e) => onLabelChange(option.id, e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              onEnter(option.id)
            }
          }}
          placeholder={`Option ${index + 1}`}
          aria-label={`Option ${index + 1}`}
          aria-invalid={isTooLong}
          css={css`
            font-size: 14px !important;
            height: 40px !important;
            border-radius: 8px;
            border: 1px solid
              ${isTooLong ? colors.ERROR_300 : colors.SECONDARY_100};
            background: ${colors.SECONDARY_200};
            color: ${colors.WHITE};
          `}
        />
      </div>
      {canDelete && (
        <button
          type='button'
          aria-label={`Delete option ${index + 1}`}
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            width: 32px;
            height: 32px;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 4px;
            &:hover {
              background: rgba(255, 255, 255, 0.1);
            }
          `}
          onClick={() => onDelete(option.id)}
        >
          <DeleteOutlined
            css={css`
              color: ${colors.ERROR_300};
              font-size: 16px;
            `}
          />
        </button>
      )}
    </div>
  )
}

export default OptionRow
